import { CaretRightFilled } from '@ant-design/icons';
import type { MouseEvent, ReactNode } from 'react';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import Checkbox from '@/components/Checkbox/Checkbox';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';

export type OptionItemProps<T> = {
  optionKey: T;
  isSelected: boolean;
  isChildSelected?: boolean;
  name: ReactNode;
  disableCheck?: boolean;
  useDepth?: boolean;
  onCheck: (filterStatus: T, e?: MouseEvent) => void;
  onCaretClick?: () => void;
  childFilterList?: FilterOptionItemT[];
};

/**
 * 이 컴포넌트는 OptionList에 OptionItem을 렌더링합니다.
 * @param props.optionKey 해당 OptionItem
 * @param props.isSelected 현재 선택된 필터인지 여부
 * @param props.isParentSelected 해당 요소의 부모 요소가 선택되었는지 여부. 부모 요소가 선택되었을 경우 선택 불가 및 옅은 체크처리
 * @param props.name 표시되는 이름
 * @param props.onClick 클릭시 실행되는 콜백
 */

const DataTableOptionItem = <T extends unknown>(props: OptionItemProps<T>) => {
  const color = useColor();

  return (
    <Wrapper
      a="center"
      g={2}
      onClick={(e) => {
        props.useDepth ||
        (props.childFilterList && props.childFilterList.length > 0)
          ? props.onCaretClick?.()
          : props.onCheck(props.optionKey, e);
      }}
    >
      {!props.disableCheck && (
        <Checkbox
          onChange={(_, e) => props.onCheck(props.optionKey, e)}
          checked={props.isSelected}
        />
      )}

      <Flex a="center" j="space-between" style={{ width: '100%' }}>
        {typeof props.name === 'string' ? (
          <Typo
            type="label2"
            color={color.gray700}
            style={{ paddingLeft: '4px' }}
            ellipsis
          >
            {props.name}
          </Typo>
        ) : (
          props.name
        )}
      </Flex>
      {(props.useDepth ||
        (props.childFilterList && props.childFilterList.length > 0)) && (
        <CaretRightFilled
          onClick={props.onCaretClick}
          style={{
            color: props.isChildSelected ? color.primary100 : color.gray400,
          }}
        />
      )}
    </Wrapper>
  );
};

const Wrapper = styled(Flex)`
  position: relative;
  box-sizing: border-box;
  min-height: 28px;
  padding: 4px;
  cursor: pointer;
  border-radius: 4px;
  background-color: ${(props) => props.theme.colors.ND00};
  transition: all 0.2s;

  &:hover {
    background-color: ${(props) => props.theme.colors.ND04};
  }
`;

export default DataTableOptionItem;
